<template>
    <v-window-item :value="3">
        <v-card-title class="pl-2">{{ translations.userInfo[lang] }}</v-card-title>

        <v-form @submit.prevent="registrateUser(lang)">
            <v-row no-gutters>
                <v-col cols="12 pl-2">
                    <v-text-field
                        v-model="user.full_name"
                        :label="translations.fullName[lang]"
                        color="secondary"
                        clearable
                        autocomplete="new-password"
                        :prepend-inner-icon="icons.mdiAccount"
                        :success="success.fullname"
                        :error-messages="error.fullname"
                        @focus="error.fullname = ''"
                        @blur="validateFullname"
                    ></v-text-field>

                    <v-text-field
                        v-model="user.password"
                        ref="password"
                        :type="show.password ? 'text' : 'password'"
                        :label="translations.password[lang]"
                        color="secondary"
                        clearable
                        :prepend-inner-icon="icons.mdiLock"
                        :append-icon="icons.mdiEye"
                        :success="success.password"
                        :error-messages="error.password"
                        @focus="error.password = ''"
                        @blur="validatePassword"
                        @click:append="show.password = !show.password"
                    ></v-text-field>

                    <v-text-field
                        v-model="user.password_confirmation"
                        ref="confirmPassword"
                        :type="show.confirmPassword ? 'text' : 'password'"
                        :label="translations.confirmPassword[lang]"
                        color="secondary"
                        clearable
                        :prepend-inner-icon="icons.mdiLockOutline"
                        :append-icon="icons.mdiEye"
                        :success="success.confirmPassword"
                        :error-messages="error.confirmPassword"
                        @focus="error.confirmPassword = ''"
                        @blur="validateConfirmPassword"
                        @click:append="show.confirmPassword = !show.confirmPassword"
                    ></v-text-field>

                    <v-menu
                        ref="menu"
                        v-model="menu"
                        :close-on-content-click="false"
                        offset-y
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                :value="birthday"
                                :label="translations.birthDate[lang]"
                                color="secondary"
                                autocomplete="off"
                                clearable
                                hide-details
                                :prepend-inner-icon="icons.mdiCalendarSearch"
                                v-on="on"
                            ></v-text-field>
                        </template>

                        <v-date-picker
                            ref="picker"
                            :locale="locale"
                            v-model="user.birth_date"
                            :max="new Date().toISOString().substr(0, 10)"
                            :picker-date="pickerDate"
                            min="1950-01-01"
                            @change="save"
                        ></v-date-picker>
                    </v-menu>

                    <v-select
                        v-model="user.referral"
                        class="mt-4"
                        :items="referrals"
                        :item-text="`${lang}`"
                        :label="translations.referral[lang]"
                        :success="success.referral"
                        :error-messages="error.referral"
                        @focus="error.referral = ''"
                        @blur="validateReferral"
                    ></v-select>

                    <v-radio-group 
                        class="mt-4 pt-0"
                        label="Gender"
                        hide-details 
                        v-model="user.gender"
                        :error-messages="error.gender"
                        @focus="error.gender = ''"
                        @blur="validateGender"
                    >
                        <v-row no-gutters class="mt-2">
                            <v-col
                                v-for="gender in genders"
                                :key="gender.text.en"
                                cols="auto"
                                class="pr-3"
                            >
                                <v-radio color="secondary" :value="gender.value">
                                    <template v-slot:label>
                                        <v-icon
                                            v-text="gender.icon"
                                        ></v-icon>
                                        <h4
                                            class="subtitle-2"
                                            v-text="gender.text[lang]"
                                        ></h4>
                                    </template>
                                </v-radio>
                            </v-col>
                        </v-row>
                    </v-radio-group>

                    <v-row no-gutters align="center" class="mt-4">
                            <v-col cols="auto">
                                <v-checkbox
                                    v-model="user.tos"
                                    color="secondary"
                                    class="mt-3 pt-0"
                                    hide-details="auto"
                                    :success="success.tos"
                                    @focus="error.tos = ''"
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="11"
                                class="mt-3 subtitle-1 font-weight-medium"
                            >
                                {{ translations.iAccept[lang] }}
                                <a
                                    class="blue--text mx-1"
                                    target="blank"
                                    :href="termsUrl"
                                >
                                {{ translations.terms[lang] }}
                                </a>
                                {{ translations.and[lang] }}
                                <a
                                    class="blue--text ml-1"
                                    target="blank"
                                    :href="privacyPolicyUrl"
                                >
                                {{ translations.policy[lang] }}</a
                                >
                            </v-col>
                        </v-row>
                        <v-row no-gutters align="center" class="mb-8">
                            <v-col cols="auto">
                                <v-checkbox
                                    v-model="user.marketing"
                                    color="secondary"
                                    class="mt-3 pt-0"
                                    hide-details="auto"
                                    :success="success.marketing"
                                    @focus="error.marketing = ''"
                                ></v-checkbox>
                            </v-col>
                            <v-col
                                cols="11"
                                class="mt-3 subtitle-1 font-weight-medium"
                            >
                            {{ translations.iAccept[lang] }}
                                <router-link
                                    class="blue--text"
                                    to="/direct-marketing"
                                >
                            {{ translations.directMarketing[lang] }}
                                </router-link>
                            </v-col>
                        </v-row>
                </v-col>
            </v-row>

            <v-alert v-if="errorMessage" type="error">{{
                errorMessage
            }}</v-alert>

            <v-card-actions class="px-0 pl-2">
                <v-btn
                    type="submit"
                    color="secondary"
                    tile
                    class="px-5"
                    :loading="loading"
                    :disabled="!valid"
                    > {{ translations.save[lang] }}</v-btn
                >
            </v-card-actions>
        </v-form>
    </v-window-item>
</template>

<script>
import { mdiAccount, mdiLock, mdiEye, mdiLockOutline, mdiCalendarSearch, mdiGenderMale, mdiGenderFemale, mdiGenderMaleFemale } from "@mdi/js";
import { mapState, mapMutations, mapActions } from "vuex";
import validators from "@/utils/validators/user";
import translations from "@/utils/translations/register/createAccount";

export default {
    name: "CreateAccount",

    mixins: [validators, translations],

    data() {
        return {
            icons: { mdiAccount, mdiLock, mdiEye, mdiLockOutline, mdiCalendarSearch, mdiGenderMale, mdiGenderFemale, mdiGenderMaleFemale },
            show: {
                password: false,
                confirmPassword: false,
            },
            pickerDate: '1995-1-1',
            menu: false,
            referrals: [
            {en:"Internet", el: "Internet", it:"Internet"}, 
            {en:"Google", el: "Google", it:"Google"}, 
            {en:"Facebook", el: "Facebook", it:"Facebook"}, 
            {en:"Advertisement", el: "Διαφήμιση", it:"Pubblicità"}, 
            {en:"Recommendation", el: "Σύσταση", it:"Raccomandazione"}, 
            {en:"Collaboration", el: "Συνεργασία", it:"Collaborazione"}, 
            {en:"Other", el: "Άλλο", it:"Altro"}, 
            ],

            genders: [
            { text:  {en:"Male", el: "Άνδρας", it:"Uomo"}, value: "male", icon: mdiGenderMale },
            { text:  {en:"Female", el: "Γυναίκα", it:"Donna"}, value: "female", icon: mdiGenderFemale },
            { text: {en:"Other", el: "Άλλο", it:"Altro"}, value: "other", icon: mdiGenderMaleFemale }
        ]
        };
    },

    computed: {
        ...mapState("register", [
            "loading",
            "errorMessage",
            "countries",
            "storeDetails",
            "privacyPolicyUrl",
            "termsUrl",
        ]),

        lang() {
            return this.$route.params.lang;
        },

        locale() {
            if (this.lang == "el") return "el-GR";
            if (this.lang == "it") return "it-IT";
            return "en-GB";
        },
        birthday() {
            return this.user.birth_date ? new Date(this.user.birth_date).toLocaleString(this.locale, {
                year: "numeric",
                month: "numeric",
                day: "numeric"
            }) : '';
            
        },
        user: {
            get() {
                return this.$store.state.register.user;
            },

            set(val) {
                this.setUserInfo(val);
            }
        }
    },

    methods: {
        ...mapMutations("register", ["setUserInfo"]),
        ...mapActions("register", ["registrateUser"]),

        save (date) {
            this.$refs.menu.save(date)
            this.pickerDate = date;
        },
    },

    watch: {
        menu (val) {
            val && setTimeout(() => (
                // this.$refs.picker.activePicker = 'YEAR',
                this.pickerDate = null
            ))
        },
    }
};
</script>
